/**
 * Тонкая обёртка над `fetch` для `/api/*`.
 *
 *  - cookie `gms_token` едет всегда (`credentials: 'include'`);
 *  - `{ error: { code, message } }` превращается в `ApiError` с полем `code`;
 *  - `multipart/form-data` уходит без ручного `Content-Type` — его ставит браузер вместе с boundary;
 *  - файлы (xlsx, вложения) скачиваются через blob, чтобы запрос ушёл с cookie.
 */

import type { ApiErrorBody, ErrorCode, UploadFilePayload, ValidationDetail } from './types';

export const API_BASE = '/api';

/** Ответы API, которые ждут ИИ, идут долго (n8n — до 180 с) — таймаута на клиенте не ставим. */
export class ApiError extends Error {
    readonly code: ErrorCode | string;
    readonly status: number;
    readonly details: unknown;

    constructor(message: string, code: ErrorCode | string, status: number, details?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.code = code;
        this.status = status;
        this.details = details;
    }

    /** Ошибки схемы `zod`: `[{ field, message }]`. */
    get validation(): ValidationDetail[] {
        return Array.isArray(this.details) ? (this.details as ValidationDetail[]) : [];
    }
}

export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

/** Собирает `?a=1&b=2`, выбрасывая пустые значения. */
export function buildQuery(params?: QueryParams): string {
    if (!params) return '';
    const search = new URLSearchParams();
    for (const [key, raw] of Object.entries(params)) {
        const values = Array.isArray(raw) ? raw : [raw];
        for (const value of values) {
            if (value === null || value === undefined || value === '') continue;
            search.append(key, String(value));
        }
    }
    const qs = search.toString();
    return qs ? '?' + qs : '';
}

export function apiUrl(path: string, query?: QueryParams): string {
    const suffix = path.startsWith('/') ? path : '/' + path;
    return API_BASE + suffix + buildQuery(query);
}

type UnauthorizedHandler = (error: ApiError) => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Ставится AuthProvider: чистит кэш и уводит на `/login` при 401 в любом запросе. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
    unauthorizedHandler = handler;
}

const STATUS_FALLBACK: Record<number, { code: ErrorCode; message: string }> = {
    400: { code: 'BAD_REQUEST', message: 'Некорректный запрос' },
    401: { code: 'UNAUTHORIZED', message: 'Требуется вход в систему' },
    403: { code: 'FORBIDDEN', message: 'Недостаточно прав' },
    404: { code: 'NOT_FOUND', message: 'Объект не найден' },
    409: { code: 'CONFLICT', message: 'Действие невозможно в текущем состоянии дела' },
    413: { code: 'PAYLOAD_TOO_LARGE', message: 'Файл слишком большой' },
    415: { code: 'UNSUPPORTED_MEDIA', message: 'Тип файла не поддерживается' },
    422: { code: 'VALIDATION', message: 'Проверьте заполнение полей' },
    502: { code: 'UPSTREAM_ERROR', message: 'Сервис разбора обращений ответил ошибкой' },
    504: { code: 'UPSTREAM_TIMEOUT', message: 'Сервис разбора обращений не ответил вовремя' },
    500: { code: 'INTERNAL', message: 'Внутренняя ошибка сервера' },
};

async function toApiError(response: Response): Promise<ApiError> {
    const fallback = STATUS_FALLBACK[response.status] ?? {
        code: 'INTERNAL' as ErrorCode,
        message: 'Ошибка ' + response.status,
    };
    let code: string = fallback.code;
    let message = fallback.message;
    let details: unknown;
    try {
        const body = (await response.json()) as ApiErrorBody | null;
        if (body && typeof body === 'object' && body.error) {
            if (body.error.code) code = body.error.code;
            if (body.error.message) message = body.error.message;
            details = body.error.details;
        }
    } catch {
        /* тело не JSON — остаётся сообщение по статусу */
    }
    return new ApiError(message, code, response.status, details);
}

export interface RequestOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    query?: QueryParams;
    /** JSON-тело; не передавать вместе с `formData`. */
    body?: unknown;
    formData?: FormData;
    signal?: AbortSignal;
    headers?: Record<string, string>;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', query, body, formData, signal, headers } = options;
    const init: RequestInit = {
        method,
        credentials: 'include',
        signal,
        headers: { Accept: 'application/json', ...(headers ?? {}) },
    };
    if (formData) {
        init.body = formData;
    } else if (body !== undefined) {
        init.headers = { ...(init.headers as Record<string, string>), 'Content-Type': 'application/json' };
        init.body = JSON.stringify(body);
    }

    let response: Response;
    try {
        response = await fetch(apiUrl(path, query), init);
    } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
        throw new ApiError('Нет связи с сервером', 'NETWORK', 0, cause);
    }

    if (!response.ok) {
        const error = await toApiError(response);
        if (error.status === 401 && unauthorizedHandler) unauthorizedHandler(error);
        throw error;
    }

    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (!text) return undefined as T;
    try {
        return JSON.parse(text) as T;
    } catch {
        throw new ApiError('Сервер вернул не JSON', 'INTERNAL', response.status);
    }
}

export const api = {
    get<T>(path: string, query?: QueryParams, options?: RequestOptions): Promise<T> {
        return apiFetch<T>(path, { ...options, method: 'GET', query });
    },
    post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
        return apiFetch<T>(path, { ...options, method: 'POST', body });
    },
    put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
        return apiFetch<T>(path, { ...options, method: 'PUT', body });
    },
    patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
        return apiFetch<T>(path, { ...options, method: 'PATCH', body });
    },
    del<T>(path: string, options?: RequestOptions): Promise<T> {
        return apiFetch<T>(path, { ...options, method: 'DELETE' });
    },
    /** `POST /api/inbox/manual` — единственная multipart-ручка. */
    upload<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
        return apiFetch<T>(path, { ...options, method: 'POST', formData });
    },
};

/* ─────────────────────────── Файлы ─────────────────────────── */

const FILENAME_STAR = /filename\*=UTF-8''([^;]+)/i;
const FILENAME_PLAIN = /filename="?([^";]+)"?/i;

function filenameFromDisposition(header: string | null, fallback: string): string {
    if (!header) return fallback;
    const star = FILENAME_STAR.exec(header);
    if (star && star[1]) {
        try {
            return decodeURIComponent(star[1]);
        } catch {
            return star[1];
        }
    }
    const plain = FILENAME_PLAIN.exec(header);
    return plain && plain[1] ? plain[1] : fallback;
}

export interface FetchedFile {
    blob: Blob;
    filename: string;
}

/** Забирает файл (вложение, отчёт) вместе с cookie и именем из `Content-Disposition`. */
export async function fetchFile(
    path: string,
    query?: QueryParams,
    fallbackName = 'file',
): Promise<FetchedFile> {
    let response: Response;
    try {
        response = await fetch(apiUrl(path, query), { credentials: 'include' });
    } catch (cause) {
        throw new ApiError('Нет связи с сервером', 'NETWORK', 0, cause);
    }
    if (!response.ok) {
        const error = await toApiError(response);
        if (error.status === 401 && unauthorizedHandler) unauthorizedHandler(error);
        throw error;
    }
    const blob = await response.blob();
    return {
        blob,
        filename: filenameFromDisposition(response.headers.get('Content-Disposition'), fallbackName),
    };
}

/** Скачивает файл и отдаёт браузеру. Возвращает имя сохранённого файла. */
export async function downloadFile(
    path: string,
    query?: QueryParams,
    fallbackName = 'file',
): Promise<string> {
    const { blob, filename } = await fetchFile(path, query, fallbackName);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Отзываем ссылку не сразу: Safari успевает начать загрузку.
    window.setTimeout(() => URL.revokeObjectURL(url), 4000);
    return filename;
}

/** Файл в формат, который ждёт `POST /cases/:id/followup`: `{name, mime, base64}`. */
export function fileToBase64(file: File): Promise<UploadFilePayload> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new ApiError('Не удалось прочитать файл', 'BAD_REQUEST', 0));
        reader.onload = () => {
            const result = String(reader.result ?? '');
            const comma = result.indexOf(',');
            resolve({
                name: file.name,
                mime: file.type || 'application/octet-stream',
                base64: comma >= 0 ? result.slice(comma + 1) : result,
            });
        };
        reader.readAsDataURL(file);
    });
}

/**
 * Принимаются изображения (сканы, фото, HEIC), PDF, DOCX и текстовые файлы: текст читается на сервере (решение
 * владельца 28.10.2026). Старый DOC не принимается — его без LibreOffice не прочитать.
 */
export const ACCEPTED_UPLOAD_MIME =
    'image/*,.heic,.heif,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.txt,text/plain';

export const UNSUPPORTED_FILE_MESSAGE =
    'Формат DOC не поддерживается. Сохраните документ как DOCX или PDF либо вставьте текст обращения.';

/** Проверка файла до отправки — сообщение об ошибке должно появляться сразу. */
export function checkUploadFile(file: File): string | null {
    const name = file.name.toLowerCase();
    if (file.type === 'application/msword' || name.endsWith('.doc')) return UNSUPPORTED_FILE_MESSAGE;
    return null;
}
